/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box } from '@primer/react';
import { InfoGlyph } from '@upright/ui/common/generic/components/tooltip';
import {
  SdgAlignmentByLevel,
  SdgAlignmentValence,
  SdgId,
  SdgUnitsMode,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import {
  getAlignmentsOfValenceWithAdjustedValues,
  getSummedAlignment,
} from '@upright/common/model/output/scores/sdg-alignment/utils';
import { TippyWithOriginalStyles } from '@upright/ui/common/generic/components/tooltips/default-tippy';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';
import { formatAlignmentValue } from '@upright/ui/common/upright/model/output/scores/eu-taxonomy/utils';
import {
  getSdgGoalLongTitle,
  getSdgGoalShortTitle,
} from '@upright/ui/common/upright/model/output/scores/sdg-alignments/descriptions/sdg-goals';
import styled from 'styled-components';

interface Props {
  sdgId: SdgId;
  data: SdgAlignmentByLevel;
  valence: SdgAlignmentValence;
  onClose: () => void;
  unitsMode: SdgUnitsMode;
}

export function SdgDrillDownModalTitle(props: Props) {
  const { valence, unitsMode, data: alignments } = props;

  const { strong, normal, weak } = getAlignmentsOfValenceWithAdjustedValues(alignments, valence, unitsMode);
  const [strongAlignmentValue, normalAlignmentValue, weakAlignmentValue] = [
    strong.adjustedValue,
    normal.adjustedValue,
    weak.adjustedValue,
  ];

  const value = getSummedAlignment(props.data, valence);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Left>
        <Top>SDG alignment drilldown</Top>
        <Bottom>
          SDG {props.sdgId}: {getSdgGoalShortTitle(props.sdgId)}
          <TippyWithOriginalStyles
            interactive={true}
            trigger={'mouseenter focus'}
            placement={'bottom'}
            content={
              <Box fontSize={0} fontWeight="normal">
                <Box as="span" fontWeight="bold">
                  Official description of SDG {props.sdgId}
                </Box>
                : <Box as="span">{getSdgGoalLongTitle(props.sdgId)}. Source: UN.</Box>
              </Box>
            }
          >
            <span>
              <InfoIconRoot />
            </span>
          </TippyWithOriginalStyles>
        </Bottom>
      </Left>
      <Right>
        {props.unitsMode === SdgUnitsMode.SUMMARY_PERCENTAGE && <Figure>{formatAlignmentValue(value)}</Figure>}
        {props.unitsMode === SdgUnitsMode.PURE_REVENUE_SHARES && (
          <PureFigures>
            <PureFigure>
              <PureFigureTitle>Strong</PureFigureTitle> {formatAlignmentValue(strongAlignmentValue)}
            </PureFigure>
            <PureFigure>
              <PureFigureTitle>Moderate</PureFigureTitle> {formatAlignmentValue(normalAlignmentValue)}
            </PureFigure>
            <PureFigure>
              <PureFigureTitle>Weak</PureFigureTitle> {formatAlignmentValue(weakAlignmentValue)}
            </PureFigure>
          </PureFigures>
        )}
      </Right>
    </Box>
  );
}

const InfoIconRoot = styled(InfoGlyph)`
  color: ${themeGet('colors.icon.dark')};
  margin-left: 4px;
`;

const Top = styled.div`
  text-transform: uppercase;
  font-weight: bold;
  font-size: 11px;
  color: ${themeGet('colors.text.muted')};
`;

const Bottom = styled.div`
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
`;

const Left = styled.div``;

const Right = styled.div`
  display: flex;
  font-size: 22px;
  align-items: center;
`;

const PureFigures = styled.div`
  padding-right: 0.6em;
  text-align: right;
  font-size: 12px;
  line-height: 1;
`;

const PureFigureTitle = styled.span`
  font-weight: 400;
`;

const PureFigure = styled.div`
  font-weight: 600;
  & + & {
    margin-top: 0.1em;
  }
`;

const Figure = styled.div`
  display: flex;
  padding-right: 0.6em;
`;
